import React from 'react';
import { Box, Container, Typography, Grid, Paper, Avatar, Divider, Button, Chip, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageProvider';
import GavelIcon from '@mui/icons-material/Gavel';
import VerifiedIcon from '@mui/icons-material/Verified';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupIcon from '@mui/icons-material/Group';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BalanceIcon from '@mui/icons-material/Balance';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LicenseIcon from '@mui/icons-material/AssignmentTurnedIn';
import WarningIcon from '@mui/icons-material/Warning';

export const AboutUs = () => {
  const { currentLang, direction } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const floatingAnimation = {
    y: [0, -15, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // Company information with updated name and disclaimer
  const companyInfo = {
    en: {
      name: 'almaryah Legal Consultancy',
      license: 'Licensed by Creative City – Fujairah',
      licenseDesc: 'Licensed to practice legal consultancy activities, which allows providing legal advice and related administrative services without practicing law or judicial representation within the UAE.',
      address: 'Creative City – Fujairah, UAE',
      description: 'A licensed company at Creative City – Fujairah to practice legal consultancy activities, which allows providing legal advice and related administrative services without practicing law or judicial representation within the UAE.',
      disclaimer: 'Legal Disclaimer: almaryah Legal Consultancy does not represent clients before courts or judicial authorities, nor does it practice the profession of law. All services are limited to legal consultations and administrative support services only, in accordance with the license issued by Creative City – Fujairah. The company does not provide legal drafting services; any legal documents are prepared based on texts and information provided by the client.'
    },
    ar: {
      name: 'الماريه للإستشارات القانونيه',
      license: 'مرخصة من Creative City – الفجيرة',
      licenseDesc: 'مرخصة لممارسة نشاط الاستشارات القانونية، وهو نشاط يُسمح فيه بتقديم المشورة القانونية والخدمات الإدارية ذات العلاقة بدون ممارسة أعمال المحاماة أو التمثيل القضائي داخل دولة الإمارات العربية المتحدة.',
      address: 'Creative City – الفجيرة، الإمارات العربية المتحدة',
      description: 'شركة مرخصة لدى Creative City – الفجيرة لممارسة نشاط الاستشارات القانونية، وهو نشاط يُسمح فيه بتقديم المشورة القانونية والخدمات الإدارية ذات العلاقة بدون ممارسة أعمال المحاماة أو التمثيل القضائي داخل دولة الإمارات العربية المتحدة.',
      disclaimer: 'إخلاء مسؤولية: لا تمثل شركة الماريه للإستشارات القانونيه العملاء أمام المحاكم أو الجهات القضائية، ولا تمارس مهنة المحاماة. جميع الخدمات تقتصر على الاستشارات القانونية والخدمات الإدارية فقط، وفقاً للترخيص الصادر من Creative City – الفجيرة. لا تقوم الشركة بتقديم خدمات الصياغة القانونية، ويتم إعداد أي مستندات قانونية بناءً على النصوص والمعلومات المقدمة من العميل.'
    }
  };

  // Company stats
  const stats = {
    en: [
      { number: '15+', label: 'Years Experience', icon: VerifiedIcon },
      { number: '500+', label: 'Consultations', icon: GavelIcon },
      { number: '50+', label: 'Expert Consultants', icon: GroupIcon },
      { number: '98%', label: 'Client Satisfaction', icon: EmojiEventsIcon }
    ],
    ar: [
      { number: '١٥+', label: 'سنوات خبرة', icon: VerifiedIcon },
      { number: '٥٠٠+', label: 'استشارة', icon: GavelIcon },
      { number: '٥٠+', label: 'مستشار خبير', icon: GroupIcon },
      { number: '٩٨٪', label: 'رضا العملاء', icon: EmojiEventsIcon }
    ]
  };

  // Core values
  const values = {
    en: [
      { 
        title: 'Excellence', 
        description: 'We strive for excellence in every consultation, ensuring the highest quality of legal advisory services.', 
        icon: EmojiEventsIcon 
      },
      { 
        title: 'Integrity', 
        description: 'Honest and transparent legal advice you can trust. We maintain the highest ethical standards in all our dealings.', 
        icon: BalanceIcon 
      },
      { 
        title: 'Efficiency', 
        description: 'Fast and reliable administrative support to save your time and resources.', 
        icon: SpeedIcon 
      },
      { 
        title: 'Compliance', 
        description: 'Fully compliant with UAE federal and local regulations and Creative City license requirements.', 
        icon: VerifiedIcon 
      }
    ],
    ar: [
      { 
        title: 'التميز', 
        description: 'نسعى للتميز في كل استشارة، مما يضمن أعلى جودة من الخدمات الاستشارية القانونية.', 
        icon: EmojiEventsIcon 
      },
      { 
        title: 'النزاهة', 
        description: 'استشارات قانونية صادقة وشفافة يمكنك الوثوق بها. نحافظ على أعلى المعايير الأخلاقية.', 
        icon: BalanceIcon 
      },
      { 
        title: 'الكفاءة', 
        description: 'دعم إداري سريع وموثوق لتوفير وقتك ومواردك.', 
        icon: SpeedIcon 
      },
      { 
        title: 'الامتثال', 
        description: 'ملتزمون بالكامل باللوائح الاتحادية والمحلية في الإمارات ومتطلبات ترخيص Creative City.', 
        icon: VerifiedIcon 
      }
    ]
  };

  const currentInfo = companyInfo[currentLang];
  const currentStats = stats[currentLang];
  const currentValues = values[currentLang];

  return (
    <Box
      id="about"
      sx={{
        py: { xs: 8, md: 15 },
        background: isMobile 
          ? 'linear-gradient(135deg, white 0%, white 100%)' 
          : 'transparent',
        position: 'relative',
        overflow: 'hidden',
        ...(isMobile && {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '50px',
            background: 'inherit',
            borderBottomLeftRadius: '50% 100%',
            borderBottomRightRadius: '50% 100%',
            transform: 'translateY(-1px)',
            zIndex: 2000
          }
        }),
        ...(!isMobile && {
          marginTop: '-150px',
          zIndex: 10
        })
      }}
    >
      {/* Animated Background Elements */}
      {isMobile && (
        <>
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 45, 0],
              opacity: [0.1, 0.15, 0.1]
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              position: 'absolute',
              top: '5%',
              right: '5%',
              width: '300px',
              height: '300px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0) 70%)',
              zIndex: 0,
              pointerEvents: 'none'
            }}
          />

          <motion.div
            animate={floatingAnimation}
            style={{
              position: 'absolute',
              bottom: '10%',
              left: '5%',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0) 70%)',
              zIndex: 0,
              pointerEvents: 'none'
            }}
          />
        </>
      )}

      <Container 
        maxWidth="lg" 
        sx={{ 
          position: 'relative', 
          zIndex: 1,
          ...(!isMobile && {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '30px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
            p: { md: 4 }
          })
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Section Title */}
          {isMobile && (
            <motion.div variants={itemVariants}>
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                    fontWeight: 'bold',
                    color: '#1a1a2e',
                    mb: 2,
                    fontSize: { xs: '2rem', md: '2.5rem' }
                  }}
                >
                  {currentLang === 'ar' ? 'من نحن' : 'About Us'}
                </Typography>
                <Divider sx={{ 
                  width: '80px', 
                  margin: '0 auto', 
                  height: '4px', 
                  background: 'linear-gradient(90deg, #d4af37, #b8942f)',
                  borderRadius: '2px'
                }} />
              </Box>
            </motion.div>
          )}

          {/* Company Info Card */}
          <motion.div variants={itemVariants}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 4 },
                mb: 6,
                background: !isMobile ? 'transparent' : 'white',
                borderRadius: '20px',
                border: !isMobile ? 'none' : '1px solid rgba(212,175,55,0.2)',
                position: 'relative',
                overflow: 'hidden',
                ...(!isMobile && {
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                  }
                })
              }}
            >
              {isMobile && (
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '200px',
                  height: '200px',
                  background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, rgba(212,175,55,0) 70%)',
                  borderRadius: '50%',
                  transform: 'translate(30%, -30%)'
                }} />
              )}
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <BusinessIcon sx={{ color: '#d4af37', fontSize: 30, mr: direction === 'rtl' ? 0 : 2, ml: direction === 'rtl' ? 2 : 0 }} />
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                        fontWeight: 'bold',
                        color: !isMobile ? '#1a1a2e' : '#1a1a2e'
                      }}
                    >
                      {currentInfo.name}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocationOnIcon sx={{ color: '#d4af37', fontSize: 20, mr: direction === 'rtl' ? 0 : 1, ml: direction === 'rtl' ? 1 : 0 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                        color: !isMobile ? '#4a4a4a' : '#666'
                      }}
                    >
                      {currentInfo.address}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    <LicenseIcon sx={{ color: '#d4af37', fontSize: 20 }} />
                    <Chip
                      label={currentInfo.license}
                      size="small"
                      sx={{
                        bgcolor: '#d4af37',
                        color: '#0b0c1e',
                        fontWeight: 500,
                        fontSize: '0.8rem'
                      }}
                    />
                  </Box>

                  <Typography
                    variant="body1"
                    paragraph
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                      color: !isMobile ? '#4a4a4a' : '#4a4a4a',
                      lineHeight: 1.8,
                      mb: 2
                    }}
                  >
                    {currentInfo.description}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </motion.div>

          {/* Main Content */}
          <Grid container spacing={6} alignItems="center" sx={{ mb: 8 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: { xs: 300, md: 400 },
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    border: '4px solid #d4af37'
                  }}
                >
                  <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
                    alt="Legal Office"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.5s ease',
                      '&:hover': {
                        transform: 'scale(1.1)'
                      }
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(135deg, rgba(212,175,55,0.2) 0%, rgba(184,148,47,0.2) 100%)',
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                    fontWeight: 'bold',
                    color: !isMobile ? '#1a1a2e' : '#1a1a2e',
                    mb: 3
                  }}
                >
                  {currentLang === 'ar' ? 'شريكك الموثوق في الاستشارات القانونية' : 'Your Trusted Partner in Legal Consultancy'}
                </Typography>
                
                <Typography
                  variant="body1"
                  paragraph
                  sx={{
                    fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                    color: !isMobile ? '#4a4a4a' : '#4a4a4a',
                    lineHeight: 1.8,
                    fontSize: '1rem',
                    mb: 3
                  }}
                >
                  {currentInfo.licenseDesc}
                </Typography>

                <Typography
                  variant="body1"
                  paragraph
                  sx={{
                    fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                    color: !isMobile ? '#4a4a4a' : '#4a4a4a',
                    lineHeight: 1.8,
                    fontSize: '1rem'
                  }}
                >
                  {currentLang === 'ar'
                    ? 'نلتزم في شركة الماريه للإستشارات القانونيه بأعلى معايير الجودة والشفافية في تقديم الاستشارات القانونية والخدمات الإدارية، مع الالتزام الكامل بحدود النشاط المرخص لنا.'
                    : 'At almaryah Legal Consultancy, we adhere to the highest standards of quality and transparency in providing legal consultancy and administrative services, with full commitment to the boundaries of our licensed activity.'}
                </Typography>

                <Grid container spacing={2} sx={{ mt: 3 }}>
                  {currentStats.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                      <Grid item xs={6} sm={3} key={index}>
                        <motion.div
                          whileHover={{ y: -10 }}
                          transition={{ type: "spring", stiffness: 300 }}
                          style={{ height: '100%' }}
                        >
                          <Paper
                            elevation={3}
                            sx={{
                              p: 2,
                              textAlign: 'center',
                              background: 'white',
                              borderRadius: '15px',
                              height: '100%',
                              minHeight: { xs: '140px', sm: '160px' },
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}
                          >
                            <Icon sx={{ fontSize: { xs: 35, sm: 40 }, color: '#d4af37', mb: 1 }} />
                            <Typography
                              variant="h6"
                              sx={{
                                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                                fontWeight: 'bold',
                                color: '#1a1a2e',
                                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                mb: 0.5
                              }}
                            >
                              {stat.number}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                                color: '#666',
                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                lineHeight: 1.4,
                                maxWidth: '90%',
                                mx: 'auto'
                              }}
                            >
                              {stat.label}
                            </Typography>
                          </Paper>
                        </motion.div>
                      </Grid>
                    );
                  })}
                </Grid>
              </motion.div>
            </Grid>
          </Grid>

          {/* Core Values Section */}
          <Box sx={{ mt: 8 }}>
            <motion.div variants={itemVariants}>
              <Typography
                variant="h4"
                align="center"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                  fontWeight: 'bold',
                  color: !isMobile ? '#1a1a2e' : '#1a1a2e',
                  mb: 6
                }}
              >
                {currentLang === 'ar' ? 'قيمنا' : 'Our Values'}
              </Typography>
            </motion.div>

            <Grid container spacing={4}>
              {currentValues.map((value, index) => {
                const Icon = value.icon;
                return (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <motion.div
                      variants={itemVariants}
                      whileHover={{ scale: 1.05, y: -10 }}
                      transition={{ type: "spring", stiffness: 300 }}
                      style={{ height: '100%' }}
                    >
                      <Paper
                        elevation={3}
                        sx={{
                          p: 3,
                          textAlign: 'center',
                          height: '100%',
                          minHeight: '250px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          background: 'white',
                          borderRadius: '20px'
                        }}
                      >
                        <Avatar
                          sx={{
                            width: { xs: 60, md: 70 },
                            height: { xs: 60, md: 70 },
                            bgcolor: 'transparent',
                            border: '2px solid #d4af37',
                            margin: '0 auto 15px',
                            '& svg': {
                              fontSize: { xs: 30, md: 35 },
                              color: '#d4af37'
                            }
                          }}
                        >
                          <Icon />
                        </Avatar>
                        
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                            fontWeight: 'bold',
                            color: '#1a1a2e',
                            mb: 1,
                            fontSize: { xs: '1rem', md: '1.1rem' }
                          }}
                        >
                          {value.title}
                        </Typography>
                        
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                            color: '#666',
                            lineHeight: 1.6,
                            fontSize: { xs: '0.8rem', md: '0.85rem' },
                            px: 1
                          }}
                        >
                          {value.description}
                        </Typography>
                      </Paper>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Legal Disclaimer Section */}
          <motion.div variants={itemVariants}>
            <Paper
              elevation={0}
              sx={{
                mt: 8,
                p: { xs: 3, md: 4 },
                background: 'linear-gradient(135deg, rgba(212,175,55,0.1) 0%, rgba(184,148,47,0.05) 100%)',
                borderRadius: '20px',
                border: '1px solid rgba(212,175,55,0.3)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <WarningIcon sx={{ color: '#d4af37', fontSize: 40, mt: 0.5 }} />
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                      fontWeight: 'bold',
                      color: '#1a1a2e',
                      mb: 1
                    }}
                  >
                    {currentLang === 'ar' ? 'إخلاء مسؤولية قانوني' : 'Legal Disclaimer'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                      color: '#4a4a4a',
                      lineHeight: 1.8,
                      fontSize: '0.9rem'
                    }}
                  >
                    {currentInfo.disclaimer}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AboutUs;